import type { KpiUnit, KpiUnitFromQuestion, TranslatedText } from "@esgt/types"
import { formatNumber } from "./formatNumber"

/** Definition of a known measurement unit. */
type UnitDefinition = {
  /** Short label shown next to values (e.g. "kg", "MWh"). Empty for units like bool. */
  label: TranslatedText
  /**
   * Format a raw value string for display in the given locale.
   * Returns only the formatted value, **without** the unit label.
   *
   * Standard numeric units produce e.g. `"1 620"`.
   * Special units like bool produce e.g. `"Ja"`.
   */
  formatValue: (rawValue: string, lang: "nb" | "en") => string
}

/** Result of resolving a KPI unit. */
export type ResolvedUnit = {
  /** Short display label for the unit (e.g. "kg", "MWh"). Empty when the unit transforms the value instead (e.g. bool). */
  label: string
  /**
   * Format a raw value string for display, **without** the unit label.
   *
   * For registry units this delegates to `UnitDefinition.formatValue`.
   * For legacy / unknown units it applies standard number formatting.
   *
   * Use the separate `label` property when you need to display the unit
   * alongside the formatted value.
   */
  formatValue: (rawValue: string) => string
}

/**
 * Factory for the common case: format the number, label is handled separately.
 */
function numericUnit(label: string, labelNb: string | null = null): UnitDefinition {
  return {
    label: { en: label, nb: labelNb || label } as TranslatedText,
    formatValue: formatNumber,
  }
}

/**
 * Central registry of known unit identifiers.
 *
 * Each key is a stable ID referenced from KPI configs.  Unit codes returned
 * by question selects (e.g. "kg", "m3" from wasteUnitsNb) intentionally
 * match registry keys so that dynamic unit resolution works without an
 * extra mapping step.
 */
const unitRegistry: Record<string, UnitDefinition> = {
  nok: numericUnit("NOK"),
  mwh: numericUnit("MWh"),
  tco2e: numericUnit("tCO₂e"),
  bool: {
    label: { nb: "", en: "" },
    formatValue: (v, lang) => {
      if (v === "1") return lang === "nb" ? "Ja" : "Yes"
      if (v === "0") return lang === "nb" ? "Nei" : "No"
      return v
    },
  },
  kg: numericUnit("kg"),
  m2: numericUnit("m²"),
  m3: numericUnit("m³"),
  ha: numericUnit("ha"),
  pct: numericUnit("%"),
  fte: numericUnit("FTE"),
  "hc-avg": numericUnit("persons", "personer"),
  "hc-eop": numericUnit("persons", "personer"),
  pcs: numericUnit("pcs", "stk"),
  hours: numericUnit("hours", "timer"),
}

// ---- resolveUnit --------------------------------------------------------

function isFromQuestion(unit: KpiUnit): unit is KpiUnitFromQuestion {
  return typeof unit === "object" && unit !== null && "fromQuestion" in unit
}

function isTranslatedText(unit: KpiUnit): unit is TranslatedText {
  return typeof unit === "object" && unit !== null && "nb" in unit
}

/**
 * Resolve a `KpiUnit` value into a concrete label, optional definition,
 * and a ready-to-use `formatValue` function.
 *
 * Handles three shapes:
 * 1. `string`  – looked up as a key in the unit registry.
 * 2. `{ fromQuestion, inputIndex }` – the unit code is read from a
 *    question's answer values, then looked up in the registry.
 * 3. `TranslatedText` (`{ nb, en }`) – legacy / custom fallback; the
 *    text for the requested language is returned directly.
 */
export function resolveUnit(
  unit: KpiUnit,
  lang: "nb" | "en",
  answerValues?: Record<string, Record<string, string> | undefined>,
): ResolvedUnit {
  const defaultFormat = (v: string) => formatNumber(v, lang)

  // 1. Direct registry key (or unknown string)
  if (typeof unit === "string") {
    if (unit === "") return { label: "", formatValue: defaultFormat }

    const def = unitRegistry[unit]
    if (def) {
      return {
        label: def.label[lang],
        formatValue: (v) => def.formatValue(v, lang),
      }
    }

    // Unknown key – use the raw string as the label (graceful degradation)
    return { label: unit, formatValue: defaultFormat }
  }

  // 2. Dynamic from question answer
  if (isFromQuestion(unit)) {
    const answers = answerValues?.[unit.fromQuestion]
    const code = answers?.[`input_${unit.inputIndex}`] ?? unit.defaultUnit
    if (code) {
      const def = unitRegistry[code]
      if (def) {
        return {
          label: def.label[lang],
          formatValue: (v) => def.formatValue(v, lang),
        }
      }
      return { label: code, formatValue: defaultFormat }
    }
    return { label: "", formatValue: defaultFormat }
  }

  // 3. Legacy TranslatedText
  if (isTranslatedText(unit)) {
    return { label: unit[lang] || "", formatValue: defaultFormat }
  }

  return { label: "", formatValue: defaultFormat }
}
